import type { TMessage } from 'src/types';
import type { SxProps } from '@mui/material';

import { useEffect } from 'react';
import { ChatResponse } from '@xpanse/ui';

import useMediaQuery from '@mui/material/useMediaQuery';
import { Stack, ListItem, useTheme, Typography, ListItemText, ListItemAvatar } from '@mui/material';

import { KNOWLEDGE_BASES } from 'src/utils/constants';

import { ERole, EResponseType } from 'src/types';

import { Iconify } from 'src/components/iconify';
import { UserAvatar } from 'src/components/avatar';

import { useAuthContext } from 'src/auth/hooks/use-auth-context';

import { CHAT } from './chatConfig';
import ChatUserMessage from './ChatUserMessage';
import FeedbackForm from './feedback/FeedbackForm';
import AssistantAvatar from './components/AssistantAvatar';
import { useGetKnowledgeBases } from './hooks/useGetKnowledgeBases';
import { useNotifyOverflowContext } from './hooks/useNotifyOverflowContext';

type Props = {
  data: TMessage;
  index: number;
  sx?: SxProps;
};

function ChatMessageListItem({ data, sx, index }: Props) {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuthContext();
  const { knowledgeBases } = useGetKnowledgeBases();
  const { notify } = useNotifyOverflowContext();
  // const previousContentRef = useRef<string>('');

  const {
    W_MAX,
    OFFSET_USER_AVATAR_DESKTOP,
    OFFSET_ASSITANT_AVATAR_DESKTOP,
    OFFSET_USER_AVATAR_MOBILE,
  } = CHAT;

  const avatarOffset =
    data.role === ERole.USER ? OFFSET_USER_AVATAR_DESKTOP : OFFSET_ASSITANT_AVATAR_DESKTOP;

  const allKnowledgeBases = [...knowledgeBases, ...KNOWLEDGE_BASES];

  useEffect(() => {
    if (data.role === ERole.ASSISTANT) {
      notify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.content, data.role]);

  return (
    <ListItem
      component="div"
      className="chat-message-list-item"
      data-testid={`chat-message-list-item-${index}`}
      sx={{
        maxWidth: `${W_MAX + avatarOffset}px`,
        margin: '0 auto',
        position: 'relative',
        ...(mdUp && {
          right: `${avatarOffset / 2}px`,
          px: 0,
        }),
        ...(smDown && {
          right: 0,
          left: data.role === ERole.USER ? `${OFFSET_USER_AVATAR_MOBILE}px` : 0,
          px: 0,
        }),
        ...(index === 0 && {
          pt: 3,
        }),
        ...sx,
      }}
    >
      <ListItemAvatar
        sx={{
          alignSelf: 'flex-start',
        }}
        className="chat-message-list-item-avatar"
      >
        {ERole.USER === data.role ? (
          <UserAvatar
            label={`${user?.firstName?.[0]}${user?.lastName?.[0]}`}
            sx={{
              backgroundColor: 'grey.200',
            }}
          />
        ) : (
          <AssistantAvatar />
        )}
      </ListItemAvatar>
      <ListItemText className="chat-message-list-item-text">
        <Stack spacing={1}>
          <Typography
            color="text.disabled"
            variant="caption"
            className="chat-message-list-item-text-timestamp"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {data.timestamp}
            {data.model && (
              <>
                {' • '}
                <Iconify icon="lucide-bot" width={12} height={12} />
                {data.model}
              </>
            )}
            {Boolean(data.knowledgeBase?.length) && (
              <>
                {' • '}
                <Iconify icon="material-symbols-light:table-rows-outline" width={12} height={12} />
                {data.knowledgeBase
                  ?.map((id) => allKnowledgeBases.find((kb) => kb.id === id)?.name)
                  .join(', ')}
              </>
            )}
          </Typography>

          {ERole.USER === data.role ? (
            <ChatUserMessage data={data} />
          ) : (
            <>
              <ChatResponse
                content={data.content}
                isLoading={data.loading}
                error={data.type === EResponseType.ERROR ? data.content : ''}
              />
              {data.type !== EResponseType.ERROR && !data.loading && (
                <FeedbackForm
                  content={data.content}
                  messageId={data.id}
                  conversationId={data.conversationId}
                  defaultValue={data.feedback?.vote}
                />
              )}
            </>
          )}
        </Stack>
      </ListItemText>
    </ListItem>
  );
}

export default ChatMessageListItem;
